import React, { useCallback, useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { type CreateOAuth2AppPayload, type OAuth2AppCredentials, useCreateOAuth2AppMutation } from '../../../store/auth/auth'
import { addToast } from '../../../store/toast/toastSlice'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { Button } from '../../../components/Button/Button'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { IntegrationManagementHeader } from '../../Integration/IntegrationManagement/IntegrationManagementHeader'
import { AppDetailsStep } from './Steps/AppDetailsStep'
import { RedirectUrisStep } from './Steps/RedirectUrisStep'
import { ScopesStep } from './Steps/ScopesStep'
import { AppCreatedSuccess } from './AppCreatedSuccess'
import { WizardStepper } from './WizardStepper'
import styles from './CreateOAuth2AppWizard.module.scss'
import { type AppDetailsFormData } from '../components/AppDetailsFormFields'
import { DEVELOPER_DOCS_URL } from '../constants'

export interface WizardFormData {
  appDetails: AppDetailsFormData
  redirectUris: string[]
  scopes: string[]
}

const INITIAL_FORM_DATA: WizardFormData = {
  appDetails: {
    name: '',
    imageUrl: '',
    shortDescription: '',
    about: '',
    whatYouCanDo: '',
  },
  redirectUris: [''],
  scopes: [],
}

export const CreateOAuth2AppWizard: React.FC = () => {
  useDocumentTitle('Create App')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [createOAuth2App, { isLoading: isCreating }] = useCreateOAuth2AppMutation()

  const [currentStep, setCurrentStep] = useState<number>(0)
  const [formData, setFormData] = useState<WizardFormData>(INITIAL_FORM_DATA)
  const [credentials, setCredentials] = useState<OAuth2AppCredentials | null>(null)

  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  const handleDeveloperDocsClick = useCallback(() => {
    window.open(DEVELOPER_DOCS_URL, '_blank', 'noopener,noreferrer')
  }, [])

  const handleCancel = useCallback(() => {
    navigate('/account/integration-settings/oauth2apps')
  }, [navigate])

  const handleAppDetailsNext = useCallback((data: AppDetailsFormData) => {
    setFormData((prev) => ({ ...prev, appDetails: data }))
    setCurrentStep(1)
  }, [])

  const handleRedirectUrisNext = useCallback((uris: string[]) => {
    setFormData((prev) => ({ ...prev, redirectUris: uris }))
    setCurrentStep(2)
  }, [])

  const handleBack = useCallback(() => {
    setCurrentStep((prev) => prev - 1)
  }, [])

  const handleScopesBack = useCallback((scopes: string[]) => {
    setFormData((prev) => ({ ...prev, scopes }))
    setCurrentStep(1)
  }, [])

  const handleSubmit = useCallback(
    async (scopes: string[]) => {
      const updatedFormData = { ...formData, scopes }

      const description: Record<string, unknown> = {}
      if (updatedFormData.appDetails.about) {
        description.about = updatedFormData.appDetails.about
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== '')
      }
      if (updatedFormData.appDetails.whatYouCanDo) {
        description.do = updatedFormData.appDetails.whatYouCanDo
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line !== '')
      }

      const payload: CreateOAuth2AppPayload = {
        name: updatedFormData.appDetails.name,
        category: 'public_api',
        shortDescription: updatedFormData.appDetails.shortDescription,
        redirectUris: updatedFormData.redirectUris.filter((uri) => uri.trim() !== ''),
        scopes: updatedFormData.scopes,
        ...(updatedFormData.appDetails.imageUrl ? { imageUrl: updatedFormData.appDetails.imageUrl } : {}),
        ...(Object.keys(description).length > 0 ? { description } : {}),
      }

      try {
        const result = await createOAuth2App({ accountId, payload }).unwrap()
        setCredentials(result)
        setCurrentStep(3)
      } catch {
        dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to create app. Please try again.'] }))
      }
    },
    [formData, accountId, createOAuth2App, dispatch],
  )

  const developerDocsHeader =
    headerContainer && DEVELOPER_DOCS_URL
      ? createPortal(
          <IntegrationManagementHeader
            backTarget='app'
            actions={
              <Button variant='darkGhost' onClick={handleDeveloperDocsClick}>
                Developer docs
              </Button>
            }
          />,
          headerContainer,
        )
      : null

  if (currentStep === 3 && credentials) {
    return (
      <>
        {developerDocsHeader}
        <AppCreatedSuccess credentials={credentials} onContinue={handleCancel} />
      </>
    )
  }

  return (
    <div className={styles['wizard-container']}>
      {developerDocsHeader}
      <WizardStepper currentStep={currentStep} />
      {currentStep === 0 ? <AppDetailsStep initialData={formData.appDetails} onNext={handleAppDetailsNext} onCancel={handleCancel} /> : null}
      {currentStep === 1 ? <RedirectUrisStep initialUris={formData.redirectUris} onNext={handleRedirectUrisNext} onBack={handleBack} /> : null}
      {currentStep === 2 ? <ScopesStep initialScopes={formData.scopes} onSubmit={handleSubmit} onBack={handleScopesBack} isSubmitting={isCreating} /> : null}
    </div>
  )
}
